import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import gameApi, { type AdventureSummary } from '../services/GameApiService';
import AdventureCard from '../components/catalog/AdventureCard';
import HeroCharacterGallery, { type HeroCharacterPortrait } from '../components/catalog/HeroCharacterGallery';
import LoadingSpinner from '../components/LoadingSpinner';
import TestimonialsSection from '../components/catalog/TestimonialsSection';
import logoSvg from '../assets/logo.svg';
import { CATALOG_TESTIMONIALS } from '../data/adventureSocialProof';
import { trackPageVisit } from '../utils/websiteAnalytics';
import {
  loadSession,
  clearSession,
  type SavedSession,
} from '../utils/sessionPersistence';
import { loadDeviceToken, saveDeviceToken } from '../utils/deviceSession';
import type { ResumeResponse } from '../services/GameApiService';

/**
 * Página principal do catálogo público de aventuras (`game.selna.ai/`).
 *
 * - Lista todas as aventuras activas via GET /api/game/adventures.
 * - Mostra o banner "Continuar a jogar" quando há sessão gravada em localStorage.
 * - SEO: dispara `seo:ready` quando o catálogo fica pronto para captura.
 */
export default function CatalogPage() {
  const navigate = useNavigate();
  const [adventures, setAdventures] = useState<AdventureSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedSession, setSavedSession] = useState<SavedSession | null>(null);
  // Cross-device recovery (F5a): "Já joguei antes noutro dispositivo" abre
  // um input de email → startGame → /check-email. O magic link é a prova de
  // posse da inbox (não devolve token directo).
  const [showCrossDeviceForm, setShowCrossDeviceForm] = useState(false);
  const [crossDeviceEmail, setCrossDeviceEmail] = useState('');
  const [crossDeviceError, setCrossDeviceError] = useState('');
  const [isSendingCrossDevice, setIsSendingCrossDevice] = useState(false);

  // Signal to the SEO pre-renderer (Puppeteer) that the page is ready to capture.
  useEffect(() => {
    if (adventures !== null || error !== null) {
      document.dispatchEvent(new Event('seo:ready'));
    }
  }, [adventures, error]);

  // Banner server-backed (F3): no mount, se houver cookie com token, chama-se
  // o /resume para obter o progresso actual do servidor. Se o servidor renovar
  // o token (estava expirado), regrava-se o cookie. Fallback para loadSession()
  // de localStorage só quando não há cookie.
  useEffect(() => {
    trackPageVisit();
    const deviceToken = loadDeviceToken();
    if (!deviceToken) {
      setSavedSession(loadSession());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result: ResumeResponse = await gameApi.resume(deviceToken);
        if (cancelled) return;
        if (result.status === 'not_found') {
          setSavedSession(null);
          return;
        }
        // Se o servidor renovou o token, regrava o cookie.
        if (result.token) {
          saveDeviceToken(result.token);
        }
        if (!result.adventureId || !result.adventureTitle) {
          setSavedSession(null);
          return;
        }
        // O /resume não devolve lastPage — derivamos do playerStatus.
        const lastPage: SavedSession['lastPage'] =
          result.playerStatus === 'completed' ? 'complete' : 'play';
        setSavedSession({
          token: result.token ?? deviceToken,
          adventureTitle: result.adventureTitle,
          adventureId: result.adventureId,
          currentEpisode: result.currentEpisode ?? 1,
          totalEpisodes: result.totalEpisodes ?? 0,
          lastPage,
          playerStatus: (result.playerStatus ?? 'active') as 'active' | 'completed',
          savedAt: new Date().toISOString(),
        });
      } catch (err) {
        // Erro de rede/servidor — fallback para o snapshot de localStorage.
        console.warn('[CatalogPage] resume failed, falling back to localStorage:', err);
        if (!cancelled) setSavedSession(loadSession());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the catalog.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await gameApi.listAdventures();
        if (!cancelled) {
          setAdventures(data.adventures);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[CatalogPage] Failed to load adventures:', err);
          setError(err instanceof Error ? err.message : 'Erro ao carregar o catálogo');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Extract portraits from adventures for the hero gallery slideshow.
  const portraits = useMemo(() => {
    if (!adventures) return [];
    const list: HeroCharacterPortrait[] = [];
    for (const adv of adventures) {
      if (adv.initialCharacter?.avatarUrl) {
        list.push({
          key: `${adv.id}:${adv.initialCharacter.id}`,
          url: adv.initialCharacter.avatarUrl,
          name: adv.initialCharacter.name || '',
        });
      }
      for (const char of adv.charactersPreview) {
        if (char.avatarUrl) {
          list.push({
            key: `${adv.id}:${char.id}`,
            url: char.avatarUrl,
            name: char.name || '',
          });
        }
      }
    }
    return list;
  }, [adventures]);

  function handleContinue() {
    if (!savedSession) return;
    const { token, lastPage } = savedSession;
    if (lastPage === 'complete') {
      navigate(`/complete/${token}`);
    } else if (lastPage === 'waiting') {
      navigate(`/waiting/${token}`);
    } else {
      navigate(`/play/${token}`);
    }
  }

  function handleDismissSavedSession() {
    clearSession();
    setSavedSession(null);
  }

  // Cross-device recovery (F5a): envia magic link para o email e navega para
  // /check-email. O jogador prova posse da inbox abrindo o link num device
  // sem cookie.
  async function handleCrossDeviceRecover(e: React.FormEvent) {
    e.preventDefault();
    if (!crossDeviceEmail.trim()) return;
    setIsSendingCrossDevice(true);
    setCrossDeviceError('');
    try {
      await gameApi.startGame(crossDeviceEmail.trim());
      sessionStorage.setItem('gameEmail', crossDeviceEmail.trim());
      navigate('/check-email');
    } catch (err) {
      setCrossDeviceError(err instanceof Error ? err.message : 'Falha ao enviar email');
    } finally {
      setIsSendingCrossDevice(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFBF5]">
      {/* ===== HEADER WITH LOGO ===== */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/" className="inline-block">
            <img src={logoSvg} alt="Selna" className="w-32 sm:w-36 md:w-40 h-auto" />
          </Link>
        </div>
      </header>

      {/* ===== HERO + CONTINUE BANNER ===== */}
      <section className="relative overflow-hidden pt-28 pb-12 md:pt-36 md:pb-16">
        {adventures ? (
          <HeroCharacterGallery portraits={portraits} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-dark/95 via-dark/80 to-[#FFFBF5]" />
        )}
        <div className="relative z-10 px-4 max-w-6xl mx-auto">
          <div className="max-w-2xl text-center md:text-right md:ml-auto md:mr-0">
            <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-accent mb-3">
              Escolhe uma história para viver
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Aventuras Narrativas Interativas
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto md:mr-0 mb-10">
              Conversas que não queres largar. Sem sair do sofá.
            </p>

            {/* Continue banner (only when there's a saved session) */}
            {savedSession && (
              <div className="max-w-md mx-auto md:mr-0">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-5 text-left relative">
                  <button
                    onClick={handleDismissSavedSession}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Fechar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <p className="text-sm text-gray-500 mb-1">
                    {savedSession.playerStatus === 'completed'
                      ? 'Aventura completa'
                      : `Episódio ${savedSession.currentEpisode} de ${savedSession.totalEpisodes}`}
                  </p>
                  <p className="text-lg font-bold text-dark mb-3">
                    {savedSession.adventureTitle}
                  </p>
                  <button
                    onClick={handleContinue}
                    className="w-full px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-xl shadow-md hover:shadow-lg transform hover:scale-[1.01] transition-all duration-200"
                  >
                    {savedSession.playerStatus === 'completed'
                      ? 'Ver resultados'
                      : 'Continuar a jogar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== CATALOG GRID ===== */}
      <main className="px-4 py-12 md:py-16 flex-1">
        <div className="max-w-6xl mx-auto">
          {adventures === null && !error && (
            <div className="flex flex-col items-center justify-center py-16">
              <LoadingSpinner />
              <p className="text-gray-500 mt-4">A carregar aventuras...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center max-w-md mx-auto">
              <p className="text-red-700 font-semibold mb-1">Não foi possível carregar o catálogo</p>
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {adventures !== null && adventures.length === 0 && !error && (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg">
                Ainda não há aventuras activas para jogar.
              </p>
              <p className="text-gray-500 mt-2 text-sm">
                Volta mais tarde — estamos a preparar novas histórias.
              </p>
            </div>
          )}

          {adventures !== null && adventures.length > 0 && (
            <>
              <h2 className="text-2xl md:text-3xl font-bold text-dark mb-2">
                Aventuras disponíveis
              </h2>
              <p className="text-gray-600 mb-8">
                {adventures.length} {adventures.length === 1 ? 'história' : 'histórias'} para escolheres.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adventures.map((adv) => (
                  <AdventureCard key={adv.id} adventure={adv} />
                ))}
              </div>
            </>
          )}

          {/* Cross-device recovery (F5a) — "Já joguei antes noutro dispositivo".
           * Envia magic link para o email; o jogador prova posse da inbox
           * abrindo o link neste device novo. */}
          <div className="mt-12 text-center">
            {!showCrossDeviceForm ? (
              <button
                type="button"
                onClick={() => setShowCrossDeviceForm(true)}
                className="text-sm text-gray-500 hover:text-primary hover:underline"
              >
                Já joguei antes noutro dispositivo
              </button>
            ) : (
              <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm p-6 text-left">
                <h3 className="text-lg font-semibold text-dark mb-2">
                  Recuperar noutro dispositivo
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Mete o email que usaste. Enviamos um link mágico para abrires
                  a tua aventura aqui.
                </p>
                <form onSubmit={handleCrossDeviceRecover} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={crossDeviceEmail}
                    onChange={(e) => setCrossDeviceEmail(e.target.value)}
                    placeholder="o.teu@email.com"
                    required
                    disabled={isSendingCrossDevice}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={isSendingCrossDevice || !crossDeviceEmail.trim()}
                    className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSendingCrossDevice ? 'A enviar...' : 'Enviar link'}
                  </button>
                </form>
                {crossDeviceError && (
                  <p className="text-red-500 text-sm mt-2">{crossDeviceError}</p>
                )}
                <button
                  type="button"
                  onClick={() => { setShowCrossDeviceForm(false); setCrossDeviceError(''); }}
                  className="mt-3 text-sm text-gray-400 hover:text-gray-600 hover:underline"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ===== TESTIMONIALS (renderiza apenas se houver testemunhos reais) ===== */}
      <TestimonialsSection
        testimonials={CATALOG_TESTIMONIALS}
        subtitle="Feedback real de quem já experimentou as aventuras da Selna."
      />

      {/* ===== FOOTER ===== */}
      <footer className="py-8 text-center text-sm text-gray-400 border-t border-gray-100">
        <p>
          © {new Date().getFullYear()} Selna ·{' '}
          <a href="https://selna.ai" className="text-primary hover:underline">
            selna.ai
          </a>
        </p>
      </footer>
    </div>
  );
}
